import { draftRates, rituals } from './GameAgeData';
import { removeDecimalsToNumber } from './NumberFormatting';
import { DraftRate, Title } from '../@types/Calculations';
import { Book, Building, Province } from '../@types/KingdomResponse';
import { BuildPercentages, Science, SimState } from '../@types/State';

const mapTitle = (title: Title): Title => {
  switch (title) {
    case 'Lady':
      return 'Knight';
    case 'King':
    case 'Queen':
    case 'Noble Lady':
      return 'Lord';
    case 'Baroness':
      return 'Baron';
    case 'Viscountess':
      return 'Viscount';
    case 'Countess':
      return 'Count';
    case 'Marchioness':
      return 'Marquis';
    case 'Duchess':
      return 'Duke';
    case 'Princess':
      return 'Prince';
    default:
      return title;
  }
};

const getSurveyValue = (province: Province, field: string) => {
  const buildType: Building | undefined = province.survey?.buildings.find(
    (x: any) => x.name === field
  );
  if (!buildType) return 0;
  const val = buildType.pctTot;

  return val;
};

const getSurveyPercentage = (province: Province, field: string) => {
  const totalOfType = getSurveyValue(province, field);
  return totalOfType;
};

const getSciValue = (province: Province, field: string) => {
  const bookType = province.sos?.books.find((x: Book) => x.type === field);
  if (!bookType) return 0;
  const val = removeDecimalsToNumber(bookType.effect, 1);
  return val ?? 0;
};

const getSciPercentage = (province: Province, field: string) => {
  const val = getSciValue(province, field);
  return val ?? 0;
};

export const fixBuildPercentage = (simState: SimState) => {
  if (!simState.buildPercentages && simState.build) {
    simState.buildPercentages = simState.build;
  }
  return simState;
};

export const getSimStateFromProvince = (selectedProvince: Province) => {
  const newBuild: BuildPercentages = {
    barren: 0,
    homes: getSurveyPercentage(selectedProvince, 'Homes'),
    farms: getSurveyPercentage(selectedProvince, 'Farms'),
    mills: getSurveyPercentage(selectedProvince, 'Mills'),
    banks: getSurveyPercentage(selectedProvince, 'Banks'),
    trainingGrounds: getSurveyPercentage(selectedProvince, 'Training Grounds'),
    armouries: getSurveyPercentage(selectedProvince, 'Armouries'),
    barracks: getSurveyPercentage(selectedProvince, 'Barracks'),
    forts: getSurveyPercentage(selectedProvince, 'Forts'),
    castles: getSurveyPercentage(selectedProvince, 'Castles'),
    hospitals: getSurveyPercentage(selectedProvince, 'Hospitals'),
    guilds: getSurveyPercentage(selectedProvince, 'Guilds'),
    towers: getSurveyPercentage(selectedProvince, 'Towers'),
    thievesDens: getSurveyPercentage(selectedProvince, "Thieves' Dens"),
    watchTowers: getSurveyPercentage(selectedProvince, 'Watch Towers'),
    universities: getSurveyPercentage(selectedProvince, 'Universities'),
    libraries: getSurveyPercentage(selectedProvince, 'Libraries'),
    stables: getSurveyPercentage(selectedProvince, 'Stables'),
    dungeons: getSurveyPercentage(selectedProvince, 'Dungeons'),
  };
  const newSci: Science = {
    alchemy: getSciPercentage(selectedProvince, 'Alchemy'),
    tools: getSciPercentage(selectedProvince, 'Tools'),
    housing: getSciPercentage(selectedProvince, 'Housing'),
    production: getSciPercentage(selectedProvince, 'Production'),
    bookkeeping: getSciPercentage(selectedProvince, 'Bookkeeping'),
    artisan: getSciPercentage(selectedProvince, 'Artisan'),
    strategy: getSciPercentage(selectedProvince, 'Strategy'),
    siege: getSciPercentage(selectedProvince, 'Siege'),
    tactics: getSciPercentage(selectedProvince, 'Tactics'),
    valor: getSciPercentage(selectedProvince, 'Valor'),
    heroism: getSciPercentage(selectedProvince, 'Heroism'),
    resilience: getSciPercentage(selectedProvince, 'Resiience'),
    crime: getSciPercentage(selectedProvince, 'Crime'),
    channeling: getSciPercentage(selectedProvince, 'Channeling'),
    shielding: getSciPercentage(selectedProvince, 'Shielding'),
    cunning: getSciPercentage(selectedProvince, 'Cunning'),
    sorcery: getSciPercentage(selectedProvince, 'Sorcery'),
    finesse: getSciPercentage(selectedProvince, 'Finesse'),
  };
  const title: Title = mapTitle(selectedProvince.title);
  const draftRate = draftRates.find(
    (x: DraftRate) => x.name === selectedProvince.ma?.draftRate
  );
  const soldiersTraining = selectedProvince.som
    ? selectedProvince.som.training.dSpecs +
      selectedProvince.som.training.oSpecs +
      selectedProvince.som.training.elites
    : 0;
  const newState = {
    be: selectedProvince.sot?.be ?? 100,
    acres: selectedProvince.sot?.land ?? selectedProvince.land,
    money: selectedProvince.sot?.money ?? 0,
    food: selectedProvince.sot?.food ?? 0,
    runes: selectedProvince.sot?.runes ?? 0,
    soldiers: selectedProvince.sot?.soldiers ?? 0,
    wizards: selectedProvince.sot?.wizards ?? 0,
    oSpecs: selectedProvince.sot?.oSpecs ?? 0,
    dSpecs: selectedProvince.sot?.dSpecs ?? 0,
    elites: selectedProvince.sot?.elites ?? 0,
    thieves: selectedProvince.sot
      ? selectedProvince.sot.thieves +
        (selectedProvince.som?.training.thieves ?? 0)
      : 0,
    prisoners: selectedProvince.sot?.prisoners ?? 0,
    horses: selectedProvince.sot?.horses ?? 0,
    wages: selectedProvince.ma?.wages ?? 100,
    soldiersTraining: soldiersTraining,
    buildPercentages: newBuild,
    science: newSci,
    title: title,
    draftRate: draftRate ? JSON.stringify(draftRate) : '',
    ritual: JSON.stringify(rituals[0]),
  };

  return newState;
};
